import { useState, useEffect } from 'react';
import { newUid, deep_copy } from '@/util';
import { WidgetHeader } from './WidgetHeader';
import { useTranslation } from 'react-i18next';
import { encode } from '@/base64';
import { Switch, InputScore } from '@teo/components';
import { QuestionSection } from '../QuestionSection';
import retry from 'async-await-retry';
import WidgetCompetencies from './widgetOptions/WidgetCompetencies';
import SortGroupsEditContent from './SortGroupsEditContent';

export const initialSortGroups = () => ({
  score: 1,
  vraag: '',
  type: 'SortGroups',
  groups: [{ uid: newUid(10), title: '', items: [] }],
});

export const SortGroupsEdit = ({
  state,
  index,
  onModified = undefined,
  setStateFn = undefined,
  lessonId,
}) => {
  const { t } = useTranslation();

  const [groups, setGroups] = useState(state?.groups);

  const [modified, setModified] = useState(newUid(20));
  const [immediateFeedback, setImmediateFeedback] = useState(
    !!state.immediateFeedback
  );
  const [showAnswer, setShowAnswer] = useState(
    state.showAnswer !== undefined ? state.showAnswer : false
  );
  const [score, setScore] = useState(state.score || 0);
  const [questionVraag, setQuestionVraag] = useState(
    state?.vraag ? state?.vraag : null
  );
  const [antwoordVraag, setAntwoordVraag] = useState(
    state?.antwoord ? state?.antwoord : null
  );

  const [excludeScore, setExcludeScore] = useState(
    state.excludeScore !== undefined ? state.excludeScore : false
  );

  const [titleWidget, setTitleWidget] = useState(
    state?.titleWidget ? state?.titleWidget : t('widgets.type.sort_groups')
  );

  const [competencies, setCompetencies] = useState([]);

  //generate the state
  const getState = async () => {
    let newState = deep_copy(state);
    newState.immediateFeedback = immediateFeedback;
    await retry(() => {
      let questionNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-question .question_optional`
      );
      if (questionNode) {
        newState.vraag = questionNode.innerHTML;
        return Promise.resolve();
      }
      return Promise.reject();
    });

    await retry(() => {
      let answerNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-answer .question_optional`
      );
      if (answerNode) {
        newState.antwoord = answerNode.innerHTML;
        return Promise.resolve();
      }
      return Promise.reject();
    });

    newState.showAnswer = showAnswer;
    newState.score = score;
    newState.titleWidget = titleWidget;
    newState.competences = competencies;
    newState.excludeScore = excludeScore;
    newState.groups = groups;
    if (state?.group) {
      newState.titleWidgetGroup = state?.titleWidgetGroup;
    }
    return newState;
  };
  setStateFn && setStateFn(getState);

  useEffect(() => {
    onModified && onModified();
  }, [onModified, modified]);

  return (
    <div
      data-open="SOW"
      className={`flex w-full flex-col gap-4 lg:p-4`}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
      id={state.uid}
    >
      <WidgetHeader
        index={index}
        titleWidget={titleWidget}
        setTitleWidget={setTitleWidget}
      />

      <div className="">
        <div className="wg-question">
          <QuestionSection questionVraag={questionVraag} />
        </div>
        <div className="my-4 flex gap-3">
          <Switch
            checked={showAnswer}
            onChange={(isCheck) => setShowAnswer(isCheck)}
          />
          <div className="mr-4">
            {t('widgets.widgets_edit.explain_after_submit')}
          </div>
        </div>
        <div className={`${!showAnswer ? 'hidden' : ''}`}>
          <div className="wg-answer">
            <QuestionSection
              questionVraag={antwoordVraag}
              exampleAnswer={true}
            />
          </div>
        </div>
      </div>

      <SortGroupsEditContent
        value={groups}
        onChange={(next) => {
          setGroups(next);
        }}
      />
      <div className="flex flex-row items-center border-b border-t border-grey-02 py-2">
        <div className="mt-5 flex w-full flex-row items-center sm:mt-0 sm:w-auto">
          <Switch
            checked={excludeScore}
            onChange={(isCheck) => {
              setExcludeScore(isCheck);
              if (isCheck) {
                setScore(0);
              }
            }}
          />
          <div className="mr-4 border-r-2 border-grey-02 px-4">
            {t('widgets.edit.exclude_score')}
          </div>
        </div>
        <div className="mt-5 flex w-full flex-row items-center lg:mt-0 lg:w-auto">
          <div className="mr-2">{t('widgets.widgets_edit.max_score')}</div>
          <InputScore
            value={score}
            onChange={(value) => setScore(value)}
            minScore={0}
            disabled={excludeScore}
          />
        </div>
      </div>

      <div data-closed="EOW"></div>
      <WidgetCompetencies
        state={state}
        setCompetencies={setCompetencies}
        competencies={competencies}
        lessonId={lessonId}
      />
    </div>
  );
};
